// @ts-nocheck
/**
 * ツインキー装備マネージャー
 *
 * 設計書: twin_key_phase2_design.md
 * 参照: alchemy_system.md
 */

import {
  EQUIPMENT_SLOTS,
  EQUIPMENT_SLOT_UNLOCK,
  EQUIPMENT_EFFECT_TYPES,
  EQUIPMENT_BASE_DATA,
  isSlotUnlocked,
  getEquipmentData,
  calculateEquipmentStats,
} from './equipmentData';
import {
  EQUIPMENT_SETS,
  calculateSetBonuses,
  extractSetBonusEffects,
} from './setBonus';

/**
 * 装備マネージャークラス
 *
 * 責務:
 * - 装備の装着・解除
 * - 装備スロット解放状態の確認
 * - 装備によるステータス補正の計算
 * - セットボーナスの計算
 */
class EquipmentManager {
  constructor() {
    // 装備マネージャーはステートレス設計
  }

  /**
   * 装備を装着
   *
   * @param {Object} monster - モンスター
   * @param {Object} equipment - 装備アイテム
   * @returns {{ success: boolean, monster: Object, message: string, unequipped: Object|null }}
   */
  equip(monster, equipment) {
    const baseData = getEquipmentData(equipment.baseId || equipment.id);
    if (!baseData) {
      return { success: false, monster, message: '無効な装備', unequipped: null };
    }

    const slot = baseData.slot;

    // スロット解放チェック
    if (!isSlotUnlocked(slot, monster.level || 1)) {
      const requiredLevel = EQUIPMENT_SLOT_UNLOCK[slot];
      return {
        success: false,
        monster,
        message: `${this.getSlotName(slot)}スロットはLv.${requiredLevel}で解放されます`,
        unequipped: null,
      };
    }

    // 現在の装備を取得
    const currentEquipment = { ...(monster.equipment || {}) };
    const previousEquipment = currentEquipment[slot] || null;

    // 新しい装備を装着
    currentEquipment[slot] = equipment;

    return {
      success: true,
      monster: { ...monster, equipment: currentEquipment },
      message: `${monster.name}に${baseData.name}を装備した`,
      unequipped: previousEquipment,
    };
  }

  /**
   * 装備を解除
   *
   * @param {Object} monster - モンスター
   * @param {string} slot - 装備スロット
   * @returns {{ success: boolean, monster: Object, unequipped: Object|null }}
   */
  unequip(monster, slot) {
    const currentEquipment = { ...(monster.equipment || {}) };

    if (!currentEquipment[slot]) {
      return { success: false, monster, unequipped: null };
    }

    const unequipped = currentEquipment[slot];
    currentEquipment[slot] = null;

    return {
      success: true,
      monster: { ...monster, equipment: currentEquipment },
      unequipped,
    };
  }

  /**
   * 全装備を解除
   */
  unequipAll(monster) {
    const currentEquipment = monster.equipment || {};
    const unequipped = Object.values(currentEquipment).filter(Boolean);

    return {
      monster: { ...monster, equipment: {} },
      unequipped,
    };
  }

  /**
   * 装備スロット情報を取得
   *
   * @param {Object} monster - モンスター
   * @returns {Array<{ slot, unlocked, requiredLevel, equipment }>}
   */
  getSlotInfo(monster) {
    const level = monster.level || 1;
    const equipment = monster.equipment || {};

    return Object.values(EQUIPMENT_SLOTS).map((slot) => ({
      slot,
      name: this.getSlotName(slot),
      unlocked: isSlotUnlocked(slot, level),
      requiredLevel: EQUIPMENT_SLOT_UNLOCK[slot],
      equipment: equipment[slot] || null,
    }));
  }

  /**
   * 解放済みスロット数を取得
   */
  getUnlockedSlotCount(monster) {
    const level = monster.level || 1;
    return Object.values(EQUIPMENT_SLOTS).filter((slot) =>
      isSlotUnlocked(slot, level)
    ).length;
  }

  // ==================== ステータス計算 ====================

  /**
   * 装備による全ステータス補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {Object} ステータス補正
   */
  calculateEquipmentModifiers(monster) {
    const modifiers = {
      // 基本ステータス
      atkFlat: 0,
      atkPercent: 0,
      hpFlat: 0,
      hpPercent: 0,
      defFlat: 0,
      spdFlat: 0,
      allStats: 0,           // 全ステータス%

      // 戦闘効果
      critRate: 0,
      critDmg: 0,
      lifesteal: 0,
      damageBonus: 0,
      damageReduce: 0,
      skillPower: 0,
      cdReduce: 0,
      evasion: 0,            // 回避率

      // 属性系
      attributeBonuses: {},    // { 属性: 倍率 }
      attributeResists: {},    // { 属性: 耐性率 }
      attributeKillers: {},    // { 属性: 特効倍率 }

      // カテゴリ・派閥系
      categoryBonuses: {},     // { カテゴリ: 倍率 }
      factionBonuses: {},      // { 派閥: 倍率 }

      // 状態異常付与
      statusInflicts: {},      // { 状態異常: 確率 }
      burnInflict: 0,
      poisonInflict: 0,
      poisonDamage: 0,
      paralyzeInflict: 0,
      stunChance: 0,
      instantDeathInflict: 0,

      // 状態異常耐性
      statusResists: {},       // { 状態異常: 耐性率 }
      instantDeathResist: 0,

      // 回復・吸収
      healBoost: 0,
      hpDrain: 0,

      // 先制系
      preemptiveChance: 0,
      guaranteedFirst: false,

      // 特殊効果
      splitChance: 0,
      controlSuccess: 0,
      debuffPower: 0,
      forbiddenPower: 0,

      // 周回系
      expBonus: 0,
      goldBonus: 0,
      dropRateBonus: 0,
      treasureFindBonus: 0,
    };

    const equipment = monster.equipment || {};

    // 各装備の効果を集計
    for (const slot of Object.values(EQUIPMENT_SLOTS)) {
      const equip = equipment[slot];
      if (!equip) continue;

      // 装備の基本ステータス (stats) を適用
      // recipes.js で定義された baseStats → Crafter.js で stats にコピー
      if (equip.stats) {
        if (equip.stats.attack) modifiers.atkFlat += equip.stats.attack;
        if (equip.stats.hp) modifiers.hpFlat += equip.stats.hp;
        if (equip.stats.speed) modifiers.spdFlat += equip.stats.speed;
        if (equip.stats.defense) modifiers.defFlat += equip.stats.defense;
      }

      // 装備の効果 (effects) を適用
      const effects = calculateEquipmentStats(equip);
      this.applyEffectsToModifiers(effects, modifiers);
    }

    // セットボーナスを追加
    const setIds = Object.values(equipment)
      .filter(Boolean)
      .map((e) => {
        const baseData = getEquipmentData(e.baseId || e.id);
        return baseData?.setId;
      })
      .filter(Boolean);

    const setBonuses = calculateSetBonuses(setIds);
    const setEffects = extractSetBonusEffects(setBonuses);
    this.applyEffectsToModifiers(setEffects, modifiers);

    return modifiers;
  }

  /**
   * 効果をモディファイアに適用
   *
   * 両形式に対応:
   * - 旧形式: EQUIPMENT_EFFECT_TYPES ('ATK_FLAT', 'ATTRIBUTE_RESIST')
   * - 新形式: recipes.js ('attribute_resistance', 'category_boost')
   *
   * @private
   */
  applyEffectsToModifiers(effects, modifiers) {
    for (const effect of effects) {
      const type = effect.type;
      const value = effect.value || 0;

      // ===== 基本ステータス =====

      // ATK固定値
      if (type === EQUIPMENT_EFFECT_TYPES.ATK_FLAT || type === 'atk_flat') {
        modifiers.atkFlat += value;
        continue;
      }

      // ATK%
      if (type === EQUIPMENT_EFFECT_TYPES.ATK_PERCENT || type === 'atk_percent') {
        modifiers.atkPercent += value;
        continue;
      }

      // HP固定値
      if (type === EQUIPMENT_EFFECT_TYPES.HP_FLAT || type === 'hp_flat') {
        modifiers.hpFlat += value;
        continue;
      }

      // HP%
      if (type === EQUIPMENT_EFFECT_TYPES.HP_PERCENT || type === 'hp_percent') {
        modifiers.hpPercent += value;
        continue;
      }

      // DEF固定値
      if (type === EQUIPMENT_EFFECT_TYPES.DEF_FLAT || type === 'def_flat') {
        modifiers.defFlat += value;
        continue;
      }

      // SPD固定値
      if (type === EQUIPMENT_EFFECT_TYPES.SPD_FLAT || type === 'spd_flat') {
        modifiers.spdFlat += value;
        continue;
      }

      // 全ステータス%
      if (type === 'all_stats') {
        modifiers.allStats += value;
        continue;
      }

      // ===== 戦闘効果 =====

      // クリティカル率
      if (type === EQUIPMENT_EFFECT_TYPES.CRIT_RATE || type === 'critical') {
        modifiers.critRate += value;
        continue;
      }

      // クリティカルダメージ
      if (type === EQUIPMENT_EFFECT_TYPES.CRIT_DMG || type === 'critical_boost') {
        modifiers.critDmg += value;
        continue;
      }

      // ライフスティール / HP吸収
      if (type === EQUIPMENT_EFFECT_TYPES.LIFESTEAL || type === 'hp_drain' || type === 'life_steal') {
        modifiers.lifesteal += value;
        modifiers.hpDrain += value;
        continue;
      }

      // 与ダメージボーナス
      if (type === EQUIPMENT_EFFECT_TYPES.DAMAGE_BONUS || type === 'damage_bonus') {
        modifiers.damageBonus += value;
        continue;
      }

      // 被ダメージ軽減
      if (type === EQUIPMENT_EFFECT_TYPES.DAMAGE_REDUCE || type === 'damage_reduction') {
        modifiers.damageReduce += value;
        continue;
      }

      // スキル威力
      if (type === EQUIPMENT_EFFECT_TYPES.SKILL_POWER || type === 'skill_damage' || type === 'skill_power_boost') {
        modifiers.skillPower += value;
        continue;
      }

      // クールダウン短縮
      if (type === EQUIPMENT_EFFECT_TYPES.CD_REDUCE || type === 'cooldown_reduction') {
        modifiers.cdReduce += value;
        continue;
      }

      // 回避率
      if (type === 'evasion') {
        modifiers.evasion += value;
        continue;
      }

      // ===== 属性系 =====

      // 属性ダメージボーナス
      if (type === EQUIPMENT_EFFECT_TYPES.ATTRIBUTE_BONUS || type === 'attribute_damage_boost' || type === 'attribute_damage') {
        const attr = effect.attribute || '無';
        modifiers.attributeBonuses[attr] = (modifiers.attributeBonuses[attr] || 0) + value;
        continue;
      }

      // 属性耐性
      if (type === EQUIPMENT_EFFECT_TYPES.ATTRIBUTE_RESIST || type === 'attribute_resistance') {
        const attr = effect.attribute || '無';
        modifiers.attributeResists[attr] = (modifiers.attributeResists[attr] || 0) + value;
        continue;
      }

      // 属性特効
      if (type === 'attribute_killer') {
        const attr = effect.attribute || '無';
        modifiers.attributeKillers[attr] = (modifiers.attributeKillers[attr] || 0) + value;
        continue;
      }

      // ===== カテゴリ・派閥系 =====

      // カテゴリ強化
      if (type === 'category_boost' || type === 'category_damage') {
        const category = effect.category || '不明';
        modifiers.categoryBonuses[category] = (modifiers.categoryBonuses[category] || 0) + value;
        continue;
      }

      // 派閥強化
      if (type === 'faction_boost') {
        const faction = effect.faction || '不明';
        modifiers.factionBonuses[faction] = (modifiers.factionBonuses[faction] || 0) + value;
        continue;
      }

      // ===== 状態異常付与 =====

      // 汎用状態異常付与
      if (type === EQUIPMENT_EFFECT_TYPES.STATUS_INFLICT) {
        modifiers.statusInflicts[effect.statusType] =
          (modifiers.statusInflicts[effect.statusType] || 0) + value;
        continue;
      }

      // 火傷付与
      if (type === 'burn_inflict' || type === 'burn_chance') {
        modifiers.burnInflict += value;
        modifiers.statusInflicts.burn = (modifiers.statusInflicts.burn || 0) + value;
        continue;
      }

      // 毒付与
      if (type === 'poison_inflict') {
        modifiers.poisonInflict += value;
        modifiers.statusInflicts.poison = (modifiers.statusInflicts.poison || 0) + value;
        continue;
      }

      // 毒ダメージ
      if (type === 'poison_damage') {
        modifiers.poisonDamage += value;
        continue;
      }

      // 麻痺付与
      if (type === 'paralyze_inflict') {
        modifiers.paralyzeInflict += value;
        modifiers.statusInflicts.paralyze = (modifiers.statusInflicts.paralyze || 0) + value;
        continue;
      }

      // スタン確率
      if (type === 'stun_chance') {
        modifiers.stunChance += value;
        modifiers.statusInflicts.stun = (modifiers.statusInflicts.stun || 0) + value;
        continue;
      }

      // 即死付与
      if (type === 'instant_death_inflict') {
        modifiers.instantDeathInflict += value;
        modifiers.statusInflicts.instantDeath = (modifiers.statusInflicts.instantDeath || 0) + value;
        continue;
      }

      // ===== 状態異常耐性 =====

      // 汎用状態異常耐性
      if (type === EQUIPMENT_EFFECT_TYPES.STATUS_RESIST) {
        if (effect.statusType) {
          modifiers.statusResists[effect.statusType] =
            (modifiers.statusResists[effect.statusType] || 0) + value;
        } else {
          modifiers.statusResists._all = (modifiers.statusResists._all || 0) + value;
        }
        continue;
      }

      // 即死耐性
      if (type === 'instant_death_resistance') {
        modifiers.instantDeathResist += value;
        modifiers.statusResists.instantDeath = (modifiers.statusResists.instantDeath || 0) + value;
        continue;
      }

      // ===== 回復・吸収 =====

      // 回復効果強化
      if (type === 'heal_boost' || type === 'heal_received_boost') {
        modifiers.healBoost += value;
        continue;
      }

      // ===== 先制系 =====

      // 先制確率
      if (type === 'preemptive_chance') {
        modifiers.preemptiveChance += value;
        continue;
      }

      // 先制確定
      if (type === 'guaranteed_first') {
        modifiers.guaranteedFirst = true;
        continue;
      }

      // ===== 特殊効果 =====

      // 分裂確率
      if (type === 'split_chance' || type === 'split_on_death') {
        modifiers.splitChance += value;
        continue;
      }

      // 操作成功率
      if (type === 'control_success') {
        modifiers.controlSuccess += value;
        continue;
      }

      // デバフ効果
      if (type === 'debuff_power' || type === 'debuff_boost') {
        modifiers.debuffPower += value;
        continue;
      }

      // 禁忌スキル威力
      if (type === 'forbidden_power' || type === 'forbidden_knowledge') {
        modifiers.forbiddenPower += value;
        continue;
      }

      // ===== 周回系 =====

      // 経験値ボーナス
      if (type === 'exp_boost' || type === 'experience_boost') {
        modifiers.expBonus += effect.bonus || value;
        continue;
      }

      // ゴールドボーナス
      if (type === 'gold_boost') {
        modifiers.goldBonus += effect.bonus || value;
        continue;
      }

      // ドロップ率ボーナス
      if (type === 'drop_rate_boost') {
        modifiers.dropRateBonus += effect.bonus || value;
        continue;
      }

      // 宝箱発見ボーナス
      if (type === 'treasure_find_boost') {
        modifiers.treasureFindBonus += effect.bonus || value;
        continue;
      }

      // ===== 未対応の効果タイプ（ログ出力せずスキップ） =====
      // 新しい効果タイプは上記に追加してください
    }
  }

  /**
   * 有効なセットボーナスを取得
   *
   * @param {Object} monster - モンスター
   * @returns {Array<Object>} セットボーナス一覧
   */
  getActiveSetBonuses(monster) {
    const equipment = monster.equipment || {};

    const setIds = Object.values(equipment)
      .filter(Boolean)
      .map((e) => {
        const baseData = getEquipmentData(e.baseId || e.id);
        return baseData?.setId;
      })
      .filter(Boolean);

    return calculateSetBonuses(setIds);
  }

  // ==================== 個別効果取得 ====================

  /**
   * 攻撃力補正を取得
   */
  getAttackModifiers(monster) {
    const mods = this.calculateEquipmentModifiers(monster);
    return {
      flat: mods.atkFlat,
      percent: mods.atkPercent,
    };
  }

  /**
   * HP補正を取得
   */
  getHPModifiers(monster) {
    const mods = this.calculateEquipmentModifiers(monster);
    return {
      flat: mods.hpFlat,
      percent: mods.hpPercent,
    };
  }

  /**
   * 防御補正を取得
   */
  getDefenseModifier(monster) {
    const mods = this.calculateEquipmentModifiers(monster);
    return mods.defFlat;
  }

  /**
   * 速度補正を取得
   */
  getSpeedModifier(monster) {
    const mods = this.calculateEquipmentModifiers(monster);
    return mods.spdFlat;
  }

  /**
   * クリティカル率補正を取得
   */
  getCritRateModifier(monster) {
    const mods = this.calculateEquipmentModifiers(monster);
    return mods.critRate;
  }

  /**
   * 属性ダメージボーナスを取得
   */
  getAttributeDamageBonus(monster, attribute) {
    const mods = this.calculateEquipmentModifiers(monster);
    return mods.attributeBonuses[attribute] || 0;
  }

  /**
   * 状態異常付与確率ボーナスを取得
   */
  getStatusInflictBonus(monster, statusType) {
    const mods = this.calculateEquipmentModifiers(monster);
    return mods.statusInflicts[statusType] || 0;
  }

  // ==================== ユーティリティ ====================

  /**
   * スロット名を取得
   */
  getSlotName(slot) {
    const names = {
      [EQUIPMENT_SLOTS.WEAPON]: '武器',
      [EQUIPMENT_SLOTS.ARMOR]: '防具',
      [EQUIPMENT_SLOTS.ACCESSORY]: 'アクセサリ',
      [EQUIPMENT_SLOTS.SPECIAL]: '特殊',
    };
    return names[slot] || slot;
  }

  /**
   * 装備サマリーを取得（UI表示用）
   *
   * @param {Object} monster - モンスター
   * @returns {Object}
   */
  getEquipmentSummary(monster) {
    const slotInfo = this.getSlotInfo(monster);
    const modifiers = this.calculateEquipmentModifiers(monster);
    const setBonuses = this.getActiveSetBonuses(monster);

    return {
      slots: slotInfo,
      modifiers,
      setBonuses,
      totalStats: {
        attack: `+${modifiers.atkFlat} (+${Math.round(modifiers.atkPercent * 100)}%)`,
        hp: `+${modifiers.hpFlat} (+${Math.round(modifiers.hpPercent * 100)}%)`,
        defense: `+${modifiers.defFlat}`,
        speed: `+${modifiers.spdFlat}`,
        critRate: `+${Math.round(modifiers.critRate * 100)}%`,
        critDmg: `+${Math.round(modifiers.critDmg * 100)}%`,
      },
    };
  }
}

// シングルトンインスタンス
export const equipmentManager = new EquipmentManager();
export default EquipmentManager;
